import React from "react";
import { Alert, Box, Button, Chip, Typography } from "@mui/material";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";

const STATE_LABEL = { PENDING: "Needs review", CONFIRMED: "Confirmed", REJECTED: "Rejected" };

const initialsOf = (name) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join("");

/**
 * Check-ins that deserve a second look (picked from the roster, or from outside the room). Confirming keeps the student
 * present, rejecting marks them absent, and either can be reversed.
 */
export default function CheckinReviewPanel({ items, onResolve, busyId, error }) {
  const pending = items.filter((i) => i.reviewStatus === "PENDING").length;
  const heading = items.length === 0 ? "Check-ins to review" : pending > 0 ? `${pending} to review` : "All reviewed";

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 1.25 }}>
        <Typography component="h3" sx={{ fontSize: 11, fontWeight: 600, letterSpacing: ".12em", textTransform: "uppercase", color: "#7b7389" }}>
          {heading}
        </Typography>
        <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: "#7b7389" }}>{pending}</Typography>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      {items.length === 0 && (
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", p: 2.25, border: "1px dashed var(--color-hairline-cool)", borderRadius: "12px", color: "#7b7389", fontSize: 13 }}>
          <InboxOutlinedIcon sx={{ fontSize: 18 }} />
          Nothing to review. Name-picked check-ins appear here.
        </Box>
      )}

      {items.length > 0 && (
        <Box sx={{ border: "1px solid var(--color-hairline-cloud)", borderRadius: "12px" }}>
          {items.map((item, index) => {
            const name = item.name || "Unknown student";
            const busy = busyId === item.attendanceId;
            return (
              <Box key={item.attendanceId} data-testid={`review-row-${item.attendanceId}`}
                sx={{ display: "grid", gridTemplateColumns: "32px minmax(0,1fr) auto", gap: 1.5, alignItems: "center", px: 2, py: 1.5, borderTop: index ? "1px solid var(--color-hairline-cloud)" : 0 }}>
                <Box aria-hidden sx={{ width: 32, height: 32, borderRadius: "50%", bgcolor: "#f1edf7", display: "grid", placeItems: "center", fontSize: 12, fontWeight: 600 }}>
                  {initialsOf(name)}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 500, fontSize: 14 }}>{name}</Typography>
                  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.25 }}>
                    {(item.flags ?? []).map((flag) => <Chip key={flag} size="small" label={flag} />)}
                    <Chip size="small" variant="outlined" label={STATE_LABEL[item.reviewStatus] ?? item.reviewStatus} />
                  </Box>
                </Box>
                <Box sx={{ display: "flex", gap: 0.75 }}>
                  {item.reviewStatus !== "REJECTED" && (
                    <Button size="small" variant="outlined" color="inherit" disabled={busy} aria-label={`Reject ${name}`}
                      onClick={() => onResolve(item, "REJECT")} sx={{ textTransform: "none", borderColor: "var(--color-hairline-cool)" }}>Reject</Button>
                  )}
                  {item.reviewStatus !== "CONFIRMED" && (
                    <Button size="small" variant="contained" disabled={busy} aria-label={`Confirm ${name}`}
                      onClick={() => onResolve(item, "CONFIRM")} sx={{ textTransform: "none", bgcolor: "var(--color-primary)" }}>Confirm</Button>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
